import React, { useEffect, useState, useCallback } from 'react';
import adminApi from './adminApi';
import { Karte, Chip, ZahlChip, ZustellChip, Leer, Fehler, datum, datumZeit } from './bausteine';

const KANAELE = ['sms', 'whatsapp'];

const NutzerDetail = ({ id, onZurueck, onGeaendert }) => {
  const [daten, setDaten] = useState(null);
  const [entwurf, setEntwurf] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const [laedt, setLaedt] = useState(false);

  const laden = useCallback(() => {
    adminApi.user(id)
      .then((d) => {
        setDaten(d);
        setEntwurf({
          name: d.user.name || '',
          checkInName: d.user.checkInName || '',
          phoneNumber: d.user.phoneNumber || '',
          emergencyContactPhoneNumber: d.user.emergencyContactPhoneNumber || '',
          preferredChannel: d.user.preferredChannel,
          preferredChannelEmergencyContact: d.user.preferredChannelEmergencyContact,
        });
      })
      .catch((e) => setFehler(e.response?.data?.error || e.message));
  }, [id]);

  useEffect(laden, [laden]);

  const speichern = async () => {
    setLaedt(true); setFehler(null); setMeldung(null);
    try {
      await adminApi.updateUser(id, entwurf);
      setMeldung('Gespeichert.');
      laden(); onGeaendert();
    } catch (e) {
      setFehler(e.response?.data?.error || e.message);
    } finally { setLaedt(false); }
  };

  const umschalten = async (aktiv) => {
    setLaedt(true); setFehler(null);
    try { await adminApi.setActive(id, aktiv); laden(); onGeaendert(); }
    catch (e) { setFehler(e.response?.data?.error || e.message); }
    finally { setLaedt(false); }
  };

  const archivieren = async () => {
    const person = daten.user.checkInName || daten.user.name;
    // Doppelte Bestaetigung: der Dienst hoert damit auf, diesen Menschen zu
    // ueberwachen, und ein laufendes Abo wird gekuendigt.
    if (!window.confirm(
      `${person} wirklich archivieren?\n\n` +
      '· Es gehen keine Tagesmeldungen mehr raus.\n' +
      '· Es wird niemand mehr alarmiert.\n' +
      '· Ein laufendes Stripe-Abo wird gekündigt.\n\n' +
      'Der Datensatz bleibt im Archiv erhalten.'
    )) return;

    setLaedt(true);
    try {
      const r = await adminApi.archiveUser(id);
      alert(`Archiviert. Abo: ${r.abo}`);
      onGeaendert(); onZurueck();
    } catch (e) {
      setFehler(e.response?.data?.error || e.message);
      setLaedt(false);
    }
  };

  if (fehler && !daten) return <Fehler>{fehler}</Fehler>;
  if (!daten) return <Leer>Lade …</Leer>;

  const u = daten.user;
  const feld = (label, key, typ = 'text') => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={typ}
        value={entwurf[key]}
        onChange={(e) => setEntwurf({ ...entwurf, [key]: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
    </div>
  );
  const auswahl = (label, key) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={entwurf[key]}
        onChange={(e) => setEntwurf({ ...entwurf, [key]: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        {KANAELE.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-6">
      <button onClick={onZurueck} className="text-sm text-blue-600 hover:underline">
        ← Zurück zur Liste
      </button>

      <Karte
        titel={u.checkInName ? `${u.checkInName} (Konto: ${u.name})` : u.name}
        rechts={<div className="flex gap-2">
          <ZahlChip status={u.paymentStatus} />
          <Chip farbe={u.isSubscribedToDailyCheckIn ? 'gruen' : 'grau'}>
            {u.isSubscribedToDailyCheckIn ? 'aktiv' : 'pausiert'}
          </Chip>
        </div>}
      >
        <Fehler>{fehler}</Fehler>
        {meldung && <p className="text-sm text-green-700 mb-3">{meldung}</p>}

        <div className="grid sm:grid-cols-2 gap-4">
          {feld('Name des Kontoinhabers', 'name')}
          {feld('Name der betreuten Person (leer = Selbstanmeldung)', 'checkInName')}
          {feld('Nummer für die Tagesmeldung', 'phoneNumber')}
          {feld('Nummer des Notfallkontakts', 'emergencyContactPhoneNumber')}
          {auswahl('Kanal Tagesmeldung', 'preferredChannel')}
          {auswahl('Kanal Notfallkontakt', 'preferredChannelEmergencyContact')}
        </div>

        <div className="mt-4 text-xs text-gray-500 space-y-1">
          <p>E-Mail: {u.email} · Land: {u.country} · Angelegt: {datum(u.createdAt)}</p>
          {u.optOutAt && (
            <p className="text-amber-700">
              Abgemeldet am {datumZeit(u.optOutAt)} über {u.optOutChannel}
            </p>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={speichern} disabled={laedt}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            Speichern
          </button>
          <button onClick={() => umschalten(!u.isSubscribedToDailyCheckIn)} disabled={laedt}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
            {u.isSubscribedToDailyCheckIn ? 'Pausieren' : 'Wieder aktivieren'}
          </button>
          <button onClick={archivieren} disabled={laedt}
            className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm ml-auto">
            Archivieren
          </button>
        </div>
      </Karte>

      <Karte titel="Antwortquote">
        <div className="flex gap-8 text-sm">
          {[daten.quote14, daten.quote30].map((q) => (
            <div key={q.tage}>
              <p className="text-2xl font-semibold">
                {q.quote === null ? '–' : `${q.quote}%`}
              </p>
              <p className="text-gray-500">
                {q.beantwortet} von {q.gesendet} · {q.tage} Tage
              </p>
            </div>
          ))}
        </div>
        {daten.quote14.gesendet >= 7 && daten.quote14.beantwortet === 0 && (
          <p className="mt-3 text-sm text-red-700">
            Keine einzige Antwort in 14 Tagen. Kanal prüfen — genau so sah der
            WhatsApp-Ausfall aus.
          </p>
        )}
      </Karte>

      <Karte titel="Verlauf">
        {daten.verlauf.length === 0 ? <Leer>Keine Meldungen.</Leer> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">Datum</th>
                  <th className="py-2 pr-4 font-medium">Kanal</th>
                  <th className="py-2 pr-4 font-medium">Zustellung</th>
                  <th className="py-2 pr-4 font-medium">Antwort</th>
                  <th className="py-2 font-medium">Alarm</th>
                </tr>
              </thead>
              <tbody>
                {daten.verlauf.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50">
                    <td className="py-2 pr-4 whitespace-nowrap">{datumZeit(c.sentAt)}</td>
                    <td className="py-2 pr-4">{c.kanal || '–'}</td>
                    <td className="py-2 pr-4"><ZustellChip status={c.zustellung} /></td>
                    <td className="py-2 pr-4">
                      {c.beantwortet ? <Chip farbe="gruen">ja</Chip> : <Chip farbe="grau">nein</Chip>}
                    </td>
                    <td className="py-2">
                      {c.alarmUebersprungen
                        ? <Chip farbe="rot" title={c.alarmUebersprungen}>übersprungen</Chip>
                        : c.alarmiert ? <Chip farbe="gelb">{c.alarmKanal}</Chip>
                        : <span className="text-gray-400">–</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
};

const TabNutzer = () => {
  const [liste, setListe] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(null);

  const laden = useCallback(() => {
    adminApi.users().then(setListe)
      .catch((e) => setFehler(e.response?.data?.error || e.message));
  }, []);
  useEffect(laden, [laden]);

  if (gewaehlt) {
    return <NutzerDetail id={gewaehlt} onZurueck={() => setGewaehlt(null)} onGeaendert={laden} />;
  }
  if (fehler) return <Fehler>{fehler}</Fehler>;
  if (!liste) return <Leer>Lade …</Leer>;

  return (
    <Karte titel={`Konten (${liste.anzahl})`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Person</th>
              <th className="py-2 pr-4 font-medium">Art</th>
              <th className="py-2 pr-4 font-medium">Kanal</th>
              <th className="py-2 pr-4 font-medium">Antwortquote 30 T.</th>
              <th className="py-2 pr-4 font-medium">Zahlung</th>
              <th className="py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {liste.users.map((u) => (
              <tr
                key={u.id}
                onClick={() => setGewaehlt(u.id)}
                className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
              >
                <td className="py-3 pr-4">
                  <span className="font-medium">{u.betreutePerson || u.name}</span>
                  <span className="block text-xs text-gray-500">{u.email}</span>
                </td>
                <td className="py-3 pr-4">
                  {u.istFremdanmeldung
                    ? <Chip farbe="blau">für Angehörige</Chip>
                    : <Chip farbe="grau">selbst</Chip>}
                </td>
                <td className="py-3 pr-4">{u.preferredChannel}</td>
                <td className="py-3 pr-4">
                  {u.quote.quote === null ? '–' : (
                    <span className={u.quote.quote === 0 && u.quote.gesendet >= 7
                      ? 'text-red-700 font-semibold' : ''}>
                      {u.quote.quote}% <span className="text-gray-400">({u.quote.gesendet})</span>
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4"><ZahlChip status={u.paymentStatus} /></td>
                <td className="py-3">
                  <Chip farbe={u.aktiv ? 'gruen' : 'grau'}>{u.aktiv ? 'aktiv' : 'pausiert'}</Chip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  );
};

export default TabNutzer;
